import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Headline, Layout, Page, Tag } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { ApiGroupModel, ApiModel, DOCUMENTATION_API_TOKEN } from '../../api/documentation.api';
import { PageSpinner } from '../../common';
import { ApiListSidebar } from './api-list-sidebar';
import * as Styles from './docs.module.scss';

const ApiTable: FC<{ apis: ApiModel[] }> = ({ apis }) => (
    <table className={Styles.simple}>
        <tbody>
            {apis.map(api => (
                <tr key={api.id}>
                    <td className={Styles.api}>
                        <Link className="Link Link--blue" to={`/docs/apis/${api.id}`}>
                            {api.displayName}
                        </Link>
                        {api.isBeta && (
                            <Tag
                                className="m-l-1"
                                color="info"
                                title="API may be updated at any time and may not become generally available. Additional request limits may be applied."
                                compact
                            >
                                beta
                            </Tag>
                        )}
                    </td>
                    <td>{api.description}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

export const ApiListPage: FC = () => {
    const [documentationApi] = useDependencies(DOCUMENTATION_API_TOKEN);
    const [apiGroups, setApiGroups] = useState<ApiGroupModel[]>([]);

    useEffect(() => {
        documentationApi.getApis().then(setApiGroups);
    }, [documentationApi]);

    return (
        <Page header={<Headline size="large">Reference</Headline>} sidebar={<ApiListSidebar />}>
            <Layout>
                {apiGroups.length === 0 ? (
                    <PageSpinner />
                ) : apiGroups.length === 1 ? (
                    <Layout.Section>
                        <ApiTable apis={apiGroups[0].apis} />
                    </Layout.Section>
                ) : (
                    apiGroups.map(apiGroup => (
                        <Layout.Section key={apiGroup.displayName}>
                            <Headline>{apiGroup.displayName.replace(/ API$/, '')}</Headline>
                            <ApiTable apis={apiGroup.apis} />
                        </Layout.Section>
                    ))
                )}
            </Layout>
        </Page>
    );
};
ApiListPage.displayName = 'ApiListPage';
